import { useNavigate } from "react-router-dom";
import { motion } from "motion/react";
import { ArrowRight } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import HeroWebGL from "./HeroWebGL";
import HeroMockup from "./HeroMockup";
import { staggerContainer, staggerItem } from "@/lib/motion-variants";
import GlowCTA from "../shared/effects/GlowCTA";
import RippleButton from "../shared/kokonut/RippleButton";

export const Hero = () => {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const isRtl = lang === "ar";

  return (
    <section
      className="relative z-0 overflow-hidden min-h-[900px] flex flex-col items-center -mt-24 pt-36 pb-20 md:-mt-32 md:pt-44"
      data-testid="hero-section"
    >
      <HeroWebGL />

      <motion.div
        variants={staggerContainer(110)}
        initial="hidden"
        animate="show"
        className="relative mx-auto w-full max-w-4xl px-4 sm:px-6 md:px-8 flex flex-col items-center text-center"
      >
        <motion.div variants={staggerItem} className="mb-6">
          <span className="inline-flex items-center gap-2 rounded-full border border-soft bg-surface-subtle px-4 py-1.5 text-[11px] mono-label text-mid">
            <span className="rounded-full bg-cyan-400/15 text-cyan-300 px-2 py-0.5 text-[10px] font-semibold">
              {lang === "en" ? "New" : "جديد"}
            </span>
            {t.hero.badge}
          </span>
        </motion.div>

        <h1 className="relative z-10 overflow-visible text-4xl sm:text-5xl md:text-6xl lg:text-[72px] font-bold tracking-tight leading-[1.2] pb-2 text-strong">
          <motion.span variants={staggerItem} className="block">{t.hero.title_1}</motion.span>
          <motion.span variants={staggerItem} className="block">
            {t.hero.title_2} <span className="gradient-text">{t.hero.title_3}</span>
          </motion.span>
        </h1>

        <motion.p variants={staggerItem} className="mt-10 text-base md:text-lg text-mid max-w-2xl leading-relaxed">
          {t.hero.subtitle}
        </motion.p>

        <motion.div variants={staggerItem} className="mt-8 flex flex-wrap items-center justify-center gap-4">
          <GlowCTA
            to="/start-project"
            data-testid="hero-cta-primary"
            className="bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-semibold px-6 py-3 rounded-lg transition-colors shadow-[0_0_30px_rgba(0,229,255,0.35)] hover:shadow-[0_0_50px_rgba(0,229,255,0.55)]"
          >
            {t.hero.cta_primary}
            <ArrowRight className={`w-4 h-4 transition ${isRtl ? "rotate-180" : ""}`} />
          </GlowCTA>

          <RippleButton
            onClick={() => navigate("/services")}
            data-testid="hero-cta-secondary"
            className="!bg-transparent hover:!bg-surface-hover border border-soft hover:border-cyan-400/50 !text-strong font-medium"
          >
            {t.hero.cta_secondary}
          </RippleButton>
        </motion.div>

        <motion.p variants={staggerItem} className="mt-6 text-[13px] text-soft">
          {t.hero.trust}
        </motion.p>
      </motion.div>

      <div className="relative w-full max-w-7xl px-4 sm:px-6 md:px-8">
        <HeroMockup />
      </div>
    </section>
  );
};

export default Hero;
